import ENV from "./Environment";
import Requests from "./Requests";
import { getBuiltInQaAdminChecklistSeed } from "./qaDefaultChecklistSeed";

const getApiBaseUrl = () => {
  const rawBaseUrl = (ENV.base_url || "").trim().replace(/\/+$/, "");
  if (!rawBaseUrl) {
    return "";
  }
  if (rawBaseUrl.endsWith("/api/v1")) {
    return rawBaseUrl;
  }
  return `${rawBaseUrl}/api/v1`;
};

const baseUrl = `${getApiBaseUrl()}/admin`;

/** Session cache so revisiting QA Admin Setup is instant (stale-while-revalidate on mount). */
let qaAdminHeadsCache = {
  userKey: null,
  heads: null,
};

const QA_ADMIN_HEADS_STORAGE_PREFIX = "qaAdminHeadsCache:v1:";

const getQaAdminCacheUserKey = (userData) => {
  const token = userData?.jwtToken;
  if (!token) {
    return null;
  }
  const label = String(userData?.username || userData?.email || userData?.id || "").trim();
  return `${label}::${String(token).slice(0, 64)}`;
};

const getQaAdminHeadsStorageKey = (userKey) =>
  `${QA_ADMIN_HEADS_STORAGE_PREFIX}${encodeURIComponent(userKey)}`;

const readHeadsFromSessionStorage = (userKey) => {
  if (!userKey || typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(getQaAdminHeadsStorageKey(userKey));
    if (raw == null || !String(raw).trim()) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return null;
    }
    return normalizeQaAdminHeadsForUi(parsed);
  } catch {
    return null;
  }
};

const writeHeadsToSessionStorage = (userKey, heads) => {
  if (!userKey || typeof sessionStorage === "undefined" || !Array.isArray(heads)) {
    return;
  }
  try {
    sessionStorage.setItem(getQaAdminHeadsStorageKey(userKey), JSON.stringify(heads));
  } catch {
    // quota or private mode — in-memory cache still works until reload
  }
};

const clearAllQaAdminHeadsSessionStorage = () => {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(QA_ADMIN_HEADS_STORAGE_PREFIX)) {
        sessionStorage.removeItem(k);
      }
    }
  } catch {
    // ignore
  }
};

const cloneHeads = (heads) => JSON.parse(JSON.stringify(heads));

const normalizeQaAdminHeadsForUi = (heads) =>
  heads.map((head) => ({
    ...head,
    lastSyncedTitle:
      head.lastSyncedTitle != null && String(head.lastSyncedTitle).trim() !== ""
        ? String(head.lastSyncedTitle).trim()
        : (head.title || "").trim(),
  }));

const QA_ADMIN_HEADING_PRIORITY = ["brief", "audio", "design", "coding"];

const getQaAdminHeadingSortRank = (template) => {
  const rawName = String(
    template?.name || template?.title || template?.templateName || ""
  )
    .trim()
    .toLowerCase();
  if (!rawName) {
    return Number.MAX_SAFE_INTEGER;
  }
  const index = QA_ADMIN_HEADING_PRIORITY.findIndex(
    (keyword) => rawName === keyword || rawName.startsWith(`${keyword} `)
  );
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

/**
 * Returns cached item heads for this user, or null if none (first visit or other account).
 * Hydrates from sessionStorage after a full page reload (in-memory cache is empty then).
 */
const peekCachedQaAdminHeads = (userData) => {
  const key = getQaAdminCacheUserKey(userData);
  if (!key) {
    return null;
  }
  if (qaAdminHeadsCache.userKey === key && qaAdminHeadsCache.heads != null) {
    return cloneHeads(qaAdminHeadsCache.heads);
  }
  const fromStorage = readHeadsFromSessionStorage(key);
  if (fromStorage != null) {
    try {
      qaAdminHeadsCache = {
        userKey: key,
        heads: cloneHeads(fromStorage),
      };
    } catch {
      // ignore
    }
    return cloneHeads(fromStorage);
  }
  return null;
};

/**
 * Persists current UI heads so the next visit can peek without waiting on the network.
 */
const commitQaAdminHeadsCache = (userData, heads) => {
  const key = getQaAdminCacheUserKey(userData);
  if (!key || !Array.isArray(heads)) {
    return;
  }
  try {
    const cloned = cloneHeads(heads);
    qaAdminHeadsCache = {
      userKey: key,
      heads: cloned,
    };
    writeHeadsToSessionStorage(key, cloned);
  } catch {
    // ignore
  }
};

const invalidateQaAdminHeadsCache = () => {
  qaAdminHeadsCache = { userKey: null, heads: null };
  clearAllQaAdminHeadsSessionStorage();
};

const normalizeToArray = (data) => {
  if (data == null) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.content)) {
    return data.content;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  if (Array.isArray(data?.templates)) {
    return data.templates;
  }
  if (Array.isArray(data?.results)) {
    return data.results;
  }
  if (Array.isArray(data?.data?.content)) {
    return data.data.content;
  }
  if (Array.isArray(data?.data?.data)) {
    return data.data.data;
  }
  if (Array.isArray(data?._embedded?.templates)) {
    return data._embedded.templates;
  }
  return [];
};

const isPlainObject = (v) => v != null && typeof v === "object" && !Array.isArray(v);

const templateLikeId = (el) =>
  el?.templateId ??
  el?.id ??
  el?._id ??
  el?.uuid ??
  el?.template_id;

/**
 * If the API wraps the list in an unexpected shape, find the first array of template-like objects.
 */
const findTemplateArrayDeep = (data, depth = 0) => {
  if (data == null || depth > 6) {
    return [];
  }
  if (Array.isArray(data)) {
    if (
      data.length > 0 &&
      data.every(
        (el) =>
          isPlainObject(el) &&
          (el.name != null ||
            el.title != null ||
            templateLikeId(el) != null ||
            el.templateName != null)
      )
    ) {
      return data;
    }
    for (const el of data) {
      const found = findTemplateArrayDeep(el, depth + 1);
      if (found.length) {
        return found;
      }
    }
    return [];
  }
  if (isPlainObject(data)) {
    for (const key of Object.keys(data)) {
      const found = findTemplateArrayDeep(data[key], depth + 1);
      if (found.length) {
        return found;
      }
    }
  }
  return [];
};

const getItemId = (item) => {
  const id =
    item?.id ??
    item?._id ??
    item?.itemId ??
    item?.templateItemId ??
    item?.uuid ??
    item?.data?.id ??
    item?.data?._id ??
    item?.data?.itemId;
  if (id == null) {
    return null;
  }
  return String(id);
};

const getItemDisplayText = (item) => {
  const candidates = [
    item?.title,
    item?.name,
    item?.text,
    item?.label,
    item?.description,
    item?.itemTitle,
    item?.itemName,
    item?.data?.title,
    item?.data?.name,
    item?.data?.text,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return "";
};

const fetchTemplatesList = async (userData) => {
  const response = await Requests.getRequest(
    `${baseUrl}/templates`,
    userData.jwtToken,
    userData,
    false
  );
  const data = await getResponseBody(response);
  if (!response.ok) {
    throw new Error(getError(data));
  }
  let list = normalizeToArray(data);
  if (list.length === 0 && data != null && typeof data === "object") {
    list = findTemplateArrayDeep(data);
  }
  return list;
};

const looksLikeTemplateItem = (el) =>
  isPlainObject(el) &&
  (getItemId(el) != null ||
    typeof el.title === "string" ||
    typeof el.name === "string" ||
    typeof el.text === "string");

const normalizeItemsResponse = (data) => {
  if (data == null) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  const candidates = [
    data?.items,
    data?.templateItems,
    data?.checklistItems,
    data?.content,
    data?.results,
    data?.data?.items,
    data?.data?.templateItems,
    data?.data?.checklistItems,
    data?.data?.content,
    Array.isArray(data?.data) ? data.data : null,
  ];
  for (const arr of candidates) {
    if (Array.isArray(arr) && (arr.length === 0 || arr.every(looksLikeTemplateItem))) {
      return arr;
    }
  }
  return [];
};

const fetchTemplateItems = async (userData, templateId) => {
  const response = await Requests.getRequest(
    `${baseUrl}/templates/${templateId}/items`,
    userData.jwtToken,
    userData,
    false
  );
  const data = await getResponseBody(response);
  if (!response.ok) {
    throw new Error(getError(data));
  }
  return normalizeItemsResponse(data);
};

/**
 * Templates + items from admin API for QA Admin Setup (no description filter).
 */
const loadQaItemHeadsFromApi = async (userData) => {
  const templates = await fetchTemplatesList(userData);

  const sorted = [...templates].sort((a, b) => {
    const rankA = getQaAdminHeadingSortRank(a);
    const rankB = getQaAdminHeadingSortRank(b);
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    return (a?.name || a?.title || "").localeCompare(b?.name || b?.title || "", undefined, {
      sensitivity: "base",
    });
  });

  const heads = await Promise.all(
    sorted.map(async (template) => {
      const templateId = getTemplateId(template);
      if (!templateId) {
        return null;
      }
      let rawItems = [];
      try {
        rawItems = await fetchTemplateItems(userData, templateId);
      } catch {
        rawItems = [];
      }
      const items = rawItems.map((item, idx) => {
        const itemId = getItemId(item) || `${templateId}-item-${idx}`;
        const text = getItemDisplayText(item);
        return {
          id: String(itemId),
          savedItemId: String(itemId),
          text,
        };
      });
      const title =
        (template?.name || template?.title || template?.templateName || "").trim() ||
        "Untitled";
      return {
        id: String(templateId),
        title,
        lastSyncedTitle: title,
        items,
        isExpanded: false,
        isSaving: false,
        savedTemplateId: String(templateId),
        isDirty: false,
      };
    })
  );

  const filtered = heads.filter(Boolean);
  const normalized = normalizeQaAdminHeadsForUi(filtered);
  commitQaAdminHeadsCache(userData, normalized);
  return normalized;
};

const saveItemHeadBlock = async (userData, itemHead) => {
  const normalizedTitle = (itemHead.title || "").trim();
  if (!normalizedTitle) {
    throw new Error("Item head title is required.");
  }

  const templatePayload = {
    name: normalizedTitle,
    description: `QA process item head: ${normalizedTitle}`,
  };

  let templateId = itemHead.savedTemplateId
    ? String(itemHead.savedTemplateId)
    : await getExistingTemplateIdByName(userData, normalizedTitle);

  // Only PUT /templates/:id when the heading title changed vs what we last synced.
  // If lastSyncedTitle is missing (""), we must NOT call template PUT — many backends
  // replace or clear all template items on that request, which looks like "save deleted
  // every sub-item" when the user only edited sub-items from the heading card.
  const lastSyncedHeading = (itemHead.lastSyncedTitle ?? "").trim();
  const headingTitleChanged =
    Boolean(templateId) &&
    lastSyncedHeading !== "" &&
    lastSyncedHeading !== normalizedTitle;
  if (templateId) {
    if (headingTitleChanged) {
      const templateResponse = await Requests.putRequest(
        `${baseUrl}/templates/${templateId}`,
        userData.jwtToken,
        templatePayload,
        userData
      );
      const templateData = await getResponseBody(templateResponse);
      if (!templateResponse.ok) {
        throw new Error(getError(templateData));
      }
    }
  } else {
    const templateResponse = await Requests.postRequest(
      `${baseUrl}/templates`,
      userData.jwtToken,
      templatePayload,
      userData
    );
    const templateData = await getResponseBody(templateResponse);
    if (!templateResponse.ok) {
      throw new Error(getError(templateData));
    }
    templateId = getTemplateId(templateData);
  }

  if (!templateId) {
    throw new Error("Template created but no template id was returned.");
  }

  const validItems = (itemHead.items || [])
    .map((item) => ({
      ...item,
      text: (item?.text || "").trim(),
    }))
    .filter((item) => item.text.length > 0);

  const savedItems = [];
  for (let index = 0; index < validItems.length; index++) {
    const item = validItems[index];
    const itemPayload = {
      title: item.text,
      description: "",
      active: true,
      order: index,
    };
    const savedItemId = item.savedItemId ? String(item.savedItemId) : null;
    const itemResponse = savedItemId
      ? await Requests.putRequest(
        `${baseUrl}/templates/${templateId}/items/${savedItemId}`,
        userData.jwtToken,
        itemPayload,
        userData
      )
      : await Requests.postRequest(
        `${baseUrl}/templates/${templateId}/items`,
        userData.jwtToken,
        itemPayload,
        userData
      );
    const itemData = await getResponseBody(itemResponse);
    if (!itemResponse.ok) {
      throw new Error(getError(itemData));
    }
    const persistedItemId = getItemId(itemData) || savedItemId;
    savedItems.push({
      ...item,
      id: persistedItemId || item.id,
      savedItemId: persistedItemId,
    });
  }

  return {
    templateId,
    savedItemsCount: savedItems.length,
    items: savedItems,
  };
};

const getError = (data) => {
  if (!data) {
    return "Unexpected server response.";
  }
  return data.error || data.message || "An unexpected error occurred.";
};

const getResponseBody = async (response) => {
  const text = await response.clone().text();
  if (text == null || !String(text).trim()) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const getTemplateId = (templateData) => {
  if (!isPlainObject(templateData)) {
    return null;
  }
  const raw =
    templateLikeId(templateData) ??
    templateData?.data?.templateId ??
    templateData?.data?.id ??
    templateData?.data?._id;
  if (raw == null || raw === "") {
    return null;
  }
  return String(raw);
};

const getExistingTemplateIdByName = async (userData, templateName) => {
  const response = await Requests.getRequest(
    `${baseUrl}/templates`,
    userData.jwtToken,
    userData,
    false
  );
  const data = await getResponseBody(response);
  if (!response.ok) {
    return null;
  }

  let templates = normalizeToArray(data);
  if (templates.length === 0 && data != null && typeof data === "object") {
    templates = findTemplateArrayDeep(data);
  }

  const existing = templates.find(
    (template) =>
      (template?.name || "").trim().toLowerCase() ===
      templateName.trim().toLowerCase()
  );

  if (!existing) {
    return null;
  }

  return getTemplateId(existing);
};

const deleteTemplateItem = async (userData, templateId, itemId) => {
  const response = await Requests.deleteRequest(
    `${baseUrl}/templates/${templateId}/items/${itemId}`,
    userData
  );
  const data = await getResponseBody(response);
  if (!response.ok) {
    throw new Error(`Delete sub-item failed (${response.status}): ${getError(data)}`);
  }
};

const deleteTemplate = async (userData, templateId) => {
  const response = await Requests.deleteRequest(`${baseUrl}/templates/${templateId}`, userData);
  const data = await getResponseBody(response);
  if (!response.ok) {
    throw new Error(`Delete heading failed (${response.status}): ${getError(data)}`);
  }
};

/**
 * Creates templates + checklist lines from a seed array:
 * `[{ section, items: [{ item, completed? }, ...] }, ...]`.
 * Ignores `completed` when posting items. Skips a section if a template with the
 * same name already exists (case-insensitive).
 */
const importDefaultChecklistsFromSeed = async (userData, seedRows) => {
  if (!Array.isArray(seedRows)) {
    throw new Error("Invalid checklist seed data.");
  }

  invalidateQaAdminHeadsCache();

  let templatesCreated = 0;
  let sectionsSkipped = 0;
  let itemsCreated = 0;

  for (const row of seedRows) {
    const section = String(row?.section ?? "").trim();
    if (!section) {
      continue;
    }

    const existingId = await getExistingTemplateIdByName(userData, section);
    if (existingId) {
      sectionsSkipped += 1;
      continue;
    }

    const templatePayload = {
      name: section,
      description: `QA process item head: ${section}`,
    };
    const templateResponse = await Requests.postRequest(
      `${baseUrl}/templates`,
      userData.jwtToken,
      templatePayload,
      userData
    );
    const templateData = await getResponseBody(templateResponse);
    if (!templateResponse.ok) {
      throw new Error(getError(templateData));
    }
    const templateId = getTemplateId(templateData);
    if (!templateId) {
      throw new Error(`Template created for "${section}" but no id was returned.`);
    }
    templatesCreated += 1;

    const rawItems = Array.isArray(row?.items) ? row.items : [];
    let order = 0;
    for (const line of rawItems) {
      const title = String(line?.item ?? line?.text ?? "").trim();
      if (!title) {
        continue;
      }
      const itemPayload = {
        title,
        description: "",
        active: true,
        order,
      };
      const itemResponse = await Requests.postRequest(
        `${baseUrl}/templates/${templateId}/items`,
        userData.jwtToken,
        itemPayload,
        userData
      );
      const itemData = await getResponseBody(itemResponse);
      if (!itemResponse.ok) {
        throw new Error(getError(itemData));
      }
      itemsCreated += 1;
      order += 1;
    }
  }

  invalidateQaAdminHeadsCache();
  return { templatesCreated, sectionsSkipped, itemsCreated };
};

const importBuiltInDefaultQaAdminChecklists = (userData) =>
  importDefaultChecklistsFromSeed(userData, getBuiltInQaAdminChecklistSeed());

const QAProcessAdminService = {
  saveItemHeadBlock,
  loadQaItemHeadsFromApi,
  deleteTemplateItem,
  deleteTemplate,
  importDefaultChecklistsFromSeed,
  importBuiltInDefaultQaAdminChecklists,
  peekCachedQaAdminHeads,
  commitQaAdminHeadsCache,
  invalidateQaAdminHeadsCache,
};

export default QAProcessAdminService;
